// Event system types

export type EventType = 'competition' | 'training_camp' | 'exam' | 'seminar' | 'other'
export type ParticipationStatus = 'registered' | 'confirmed' | 'participated' | 'cancelled' | 'no_show'
export type ParticipantPaymentStatus = 'not_paid' | 'paid' | 'exempt'

export interface Event {
  id: string
  assistant_id: string
  organization_id?: string
  name: string
  event_type: EventType
  description?: string
  location?: string
  start_date: string
  end_date?: string
  payment_amount?: string
  payment_currency?: string
  metadata?: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface EventParticipant {
  id: string
  event_id: string
  athlete_id: string
  athlete_name?: string
  status: ParticipationStatus
  payment_status: ParticipantPaymentStatus
  result?: Record<string, unknown>
  notes?: string
  created_at: string
  updated_at: string
}

export interface AthleteEvent extends Event {
  participation_status: ParticipationStatus
  participant_payment_status: ParticipantPaymentStatus
  result?: Record<string, unknown>
  participant_notes?: string
}

// Requests

export interface CreateEventRequest {
  name: string
  event_type: EventType
  description?: string
  location?: string
  start_date: string
  end_date?: string
  payment_amount?: string
  payment_currency?: string
}

export interface UpdateEventRequest {
  name?: string
  event_type?: EventType
  description?: string
  location?: string
  start_date?: string
  end_date?: string
  payment_amount?: string
  payment_currency?: string
}

export interface AddParticipantRequest {
  athlete_id: string
  notes?: string
}

export interface UpdateParticipantRequest {
  status?: ParticipationStatus
  payment_status?: ParticipantPaymentStatus
  result?: Record<string, unknown>
  notes?: string
}

export interface BulkAddParticipantsRequest {
  athlete_ids: string[]
}

// Labels

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  competition: 'Соревнование',
  training_camp: 'Сборы',
  exam: 'Экзамен',
  seminar: 'Семинар',
  other: 'Другое',
}

export const EVENT_TYPE_ICONS: Record<EventType, string> = {
  competition: '🏆',
  training_camp: '⛺',
  exam: '📝',
  seminar: '📚',
  other: '📅',
}

export const PARTICIPATION_STATUS_LABELS: Record<ParticipationStatus, string> = {
  registered: 'Зарегистрирован',
  confirmed: 'Подтверждён',
  participated: 'Участвовал',
  cancelled: 'Отменён',
  no_show: 'Не явился',
}

export const PARTICIPATION_STATUS_COLORS: Record<ParticipationStatus, string> = {
  registered: 'text-blue-600 bg-blue-100',
  confirmed: 'text-green-600 bg-green-100',
  participated: 'text-emerald-600 bg-emerald-100',
  cancelled: 'text-gray-500 bg-gray-100',
  no_show: 'text-red-600 bg-red-100',
}

export const PARTICIPANT_PAYMENT_LABELS: Record<ParticipantPaymentStatus, string> = {
  not_paid: 'Не оплачено',
  paid: 'Оплачено',
  exempt: 'Освобождён',
}

export const PARTICIPANT_PAYMENT_COLORS: Record<ParticipantPaymentStatus, string> = {
  not_paid: 'text-red-600 bg-red-100',
  paid: 'text-green-600 bg-green-100',
  exempt: 'text-blue-600 bg-blue-100',
}

// Helpers

export function formatEventDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })
}

export function formatEventDateShort(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })
}

export function isUpcoming(event: Event): boolean {
  return new Date(event.start_date) > new Date()
}

export function isPast(event: Event): boolean {
  const checkDate = event.end_date || event.start_date
  return new Date(checkDate) < new Date()
}
